import {
  ArrowDown,
  ArrowUp,
  Heading2,
  Link2,
  Minus,
  Monitor,
  Pilcrow,
  Smartphone,
  Trash2,
} from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

import { type EmailBlock, newBlock } from "./emailBlocks";

const inputClass =
  "rounded-md border border-input bg-transparent px-2 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

const BLOCK_KINDS = [
  { type: "heading" as const, label: "Heading", icon: Heading2 },
  { type: "paragraph" as const, label: "Text", icon: Pilcrow },
  { type: "button" as const, label: "Link button", icon: Link2 },
  { type: "divider" as const, label: "Divider", icon: Minus },
];

/**
 * Block-based email composer with an honest device preview.
 *
 * The preview renders the SAME blocks the send text is derived from, it is
 * not a separate template that can drift from what goes out. The phone width
 * is 320px content (an iPhone SE viewport minus mail-client chrome), because
 * the emails that break on mobile are the ones only ever previewed at desktop
 * width.
 */
export const EmailBuilder = ({
  blocks,
  onChange,
  disabled,
}: {
  blocks: EmailBlock[];
  onChange: (next: EmailBlock[]) => void;
  disabled?: boolean;
}) => {
  const [device, setDevice] = useState<"phone" | "desktop">("phone");

  const update = (id: string, patch: Partial<EmailBlock>) =>
    onChange(
      blocks.map((block) =>
        block.id === id ? ({ ...block, ...patch } as EmailBlock) : block,
      ),
    );

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next);
  };

  const remove = (id: string) =>
    onChange(blocks.filter((block) => block.id !== id));

  // Stacked, not side-by-side: the campaign detail panel is ~425px wide next
  // to its list, so two columns would squeeze the phone preview to half its
  // real width, a "mobile preview" narrower than any phone.
  return (
    <div className="flex flex-col gap-4">
      {/* Editor column */}
      <div className="flex min-w-0 flex-col gap-2">
        {blocks.map((block, index) => (
          <div
            key={block.id}
            className="flex items-start gap-2 rounded-md border border-border-subtle p-2"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              {block.type === "heading" && (
                <input
                  className={cn(inputClass, "font-semibold")}
                  placeholder="Section heading"
                  value={block.text}
                  disabled={disabled}
                  onChange={(e) => update(block.id, { text: e.target.value })}
                />
              )}
              {block.type === "paragraph" && (
                <textarea
                  className={cn(inputClass, "min-h-16 resize-y")}
                  placeholder={"Hi {{first_name}}, …"}
                  value={block.text}
                  disabled={disabled}
                  onChange={(e) => update(block.id, { text: e.target.value })}
                />
              )}
              {block.type === "button" && (
                <div className="grid gap-1.5 sm:grid-cols-2">
                  <input
                    className={inputClass}
                    placeholder="Button label (Book now)"
                    value={block.label}
                    disabled={disabled}
                    onChange={(e) =>
                      update(block.id, { label: e.target.value })
                    }
                  />
                  <input
                    className={inputClass}
                    placeholder="https://…"
                    value={block.url}
                    disabled={disabled}
                    onChange={(e) => update(block.id, { url: e.target.value })}
                  />
                </div>
              )}
              {block.type === "divider" && (
                <div className="h-px w-full bg-border" aria-label="Divider" />
              )}
            </div>

            {!disabled && (
              <div className="flex shrink-0 flex-col gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground"
                  onClick={() => move(index, -1)}
                  aria-label="Move block up"
                >
                  <ArrowUp className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground"
                  onClick={() => move(index, 1)}
                  aria-label="Move block down"
                >
                  <ArrowDown className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground hover:text-destructive"
                  onClick={() => remove(block.id)}
                  aria-label="Remove block"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}

        {!disabled && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11.5px] text-muted-foreground">Add:</span>
            {BLOCK_KINDS.map((kind) => (
              <button
                key={kind.type}
                type="button"
                onClick={() => onChange([...blocks, newBlock(kind.type)])}
                className="flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <kind.icon className="size-3.5" />
                {kind.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Preview column */}
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] text-muted-foreground">
            Preview, exactly what the send text is built from
          </span>
          <div className="flex gap-1">
            <Button
              variant={device === "phone" ? "secondary" : "ghost"}
              size="icon"
              className="size-7"
              onClick={() => setDevice("phone")}
              aria-label="Phone preview"
            >
              <Smartphone className="size-4" />
            </Button>
            <Button
              variant={device === "desktop" ? "secondary" : "ghost"}
              size="icon"
              className="size-7"
              onClick={() => setDevice("desktop")}
              aria-label="Desktop preview"
            >
              <Monitor className="size-4" />
            </Button>
          </div>
        </div>

        <div className="flex justify-center rounded-md border border-border bg-muted/40 p-3">
          <div
            className={cn(
              "min-w-0 rounded-md border border-border bg-white p-4 text-[13px] text-neutral-900 shadow-sm transition-all",
              device === "phone" ? "w-full max-w-[320px]" : "w-full",
            )}
          >
            {blocks.length === 0 ? (
              <p className="text-neutral-400">Add a block to start writing.</p>
            ) : (
              blocks.map((block) => {
                switch (block.type) {
                  case "heading":
                    return (
                      <h3
                        key={block.id}
                        className="mb-2 mt-3 break-words text-[16px] font-semibold first:mt-0"
                      >
                        {block.text || "Heading"}
                      </h3>
                    );
                  case "paragraph":
                    return (
                      <p
                        key={block.id}
                        className="mb-3 whitespace-pre-wrap break-words leading-relaxed"
                      >
                        {block.text || "…"}
                      </p>
                    );
                  case "button":
                    return (
                      <div key={block.id} className="mb-3">
                        <span className="inline-block max-w-full truncate rounded-md bg-neutral-900 px-4 py-2 text-[13px] font-medium text-white">
                          {block.label || block.url || "Button"}
                        </span>
                        {block.url ? (
                          <span className="mt-1 block break-all text-[11px] text-neutral-500">
                            {block.url}
                          </span>
                        ) : null}
                      </div>
                    );
                  case "divider":
                    return (
                      <hr key={block.id} className="my-3 border-neutral-200" />
                    );
                }
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
